import { useState, useEffect, useMemo } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useFormik, Form, FormikProvider } from "formik";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
// material
import {
  Link,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel,
  DialogContent,
  Dialog,
  Box,
  Button,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { authService } from "src/services";
import Token from "../../../util/Token";
import enums from "src/util/enums";
import { authSlice } from "src/store/auth/auth.slice";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import { useMutation } from "@tanstack/react-query";
import { loginFormValidationSchema } from "src/util/validationSchemas";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { base64toBlob } from "src/util/convertToBase64";
import CaptchaField from "src/common/components/CaptchaField";
import { isCaptchaValid, setCaptchaVerified } from "src/util/captcha";
import ReCAPTCHA from "react-google-recaptcha";

const CAPTCHA_KEY_LOGIN = "captcha_verified_at_login";

const CustomPageLayer = ({ renderPageProps }) => {
  useEffect(() => {
    if (renderPageProps.textLayerRendered) {
      renderPageProps.markRendered(renderPageProps.pageIndex);
    }
  }, [renderPageProps.textLayerRendered]);

  return (
    <>
      {renderPageProps.svgLayer.children}
      {renderPageProps.textLayer.children}
      {renderPageProps.annotationLayer.children}
    </>
  );
};

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const { mutate, isLoading, reset } = useMutation(authService.login);
  const { mutate: tnCMutate } = useMutation(authService.getuserTnCData);
  const { mutate: updateTnCmutate } = useMutation(authService.updateTnCForUser);
  const [tnCurl, setTnCurl] = useState();
  const [username, setUsername] = useState();
  const [pdfViewOpen, setPdfViewOpen] = useState(false);
  const [tnCcheckbox, setTnCcheckbox] = useState(false);
  const [userData, setUserData] = useState();
  const [showCaptcha, setShowCaptcha] = useState(
    () => !isCaptchaValid(CAPTCHA_KEY_LOGIN)
  );
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const navigate = useNavigate();
  const lightInputStyles = useMemo(
    () => ({
      '& label': {
        color: '#5f6a8d',
        fontWeight: 600,
        textTransform: 'capitalize',
        '&.Mui-focused': {
          color: '#3d72ff',
        },
      },
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        transition: 'all 0.2s ease',
        '& fieldset': {
          borderColor: 'rgba(61, 114, 255, 0.25)',
          borderWidth: '1.5px',
        },
        '&:hover fieldset': {
          borderColor: 'rgba(61, 114, 255, 0.6)',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#3d72ff',
          boxShadow: '0 0 0 2px rgba(61, 114, 255, 0.20)',
        },
        '& input': {
          backgroundColor: '#ffffff',
        },
      },
      '& .MuiInputBase-input': {
        color: '#0f1c3f',
        fontWeight: 500,
        letterSpacing: '0.01em',
      },
      '& input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
        boxShadow: '0 0 0 1000px #ffffff inset',
        WebkitTextFillColor: '#0f1c3f',
        caretColor: '#0f1c3f',
        borderRadius: '16px',
      },
      '& .MuiInputAdornment-root .MuiIconButton-root': {
        color: '#6f7daa',
      },
    }),
    []
  );

  const checkboxLabelStyles = useMemo(
    () => ({
      color: '#34405f',
      fontWeight: 500,
    }),
    []
  );

  useEffect(() => {
    setShowCaptcha(!isCaptchaValid(CAPTCHA_KEY_LOGIN));
  }, []);

  const onLoginSuccess = (response) => {
    const loginResponse = response?.data?.data;
    if (loginResponse?.access_token) {
      Token.set(loginResponse.access_token);
      dispatch(authSlice.actions.setAuth(response?.data?.data?.user));
      setCaptchaVerified(CAPTCHA_KEY_LOGIN);
      setShowCaptcha(false);
      setTimeout(() => {
        navigate(enums.path.DASHBOARD_APP);
      }, 100); 
    } else {
      dispatch(
        alertSlice.actions.setAlert({
          severity: "error",
          open: true,
          message: "Error in login. Please try again.",
        })
      );
    }
  };

  const onLoginError = (error) => {
    dispatch(
      alertSlice.actions.setAlert({
        severity: "error",
        open: true,
        message: error?.response?.data?.message ?? error.message,
      })
    );
  };

  const onAcceptingTnC = () => {
    updateTnCmutate(
      { username: username },
      {
        onSuccess: () => {
          setPdfViewOpen(false);
          loginUser(userData, { setSubmitting });
        },
      }
    );
  };

  const loginUser = (loginData, { setSubmitting }) => {
    delete loginData.remember;
    mutate(
      {
        ...loginData,
        password: window.btoa(loginData.password),
        recaptchaToken: captchaValue,
      },
      {
        onSuccess: onLoginSuccess,
        onError: onLoginError,
        onSettled: () => reset(),
      }
    );
    setSubmitting(false);
  };

  const convertBase64String = (pdfBase64) => {
    const pdfBlob = base64toBlob(pdfBase64, "application/pdf");
    const pdfBlobUrl = URL.createObjectURL(pdfBlob);

    setTnCurl(pdfBlobUrl);

    return () => {
      URL.revokeObjectURL(pdfBlobUrl);
    };
  };

  const onSubmit = (loginData, { setSubmitting }) => {
    setUserData(loginData);
    setUsername(loginData?.username);
    tnCMutate(loginData?.username, {
      onSuccess: (response) => {
        const data = response.data.data;
        convertBase64String(data.url);
        // setTnCurl(data.url);
        if (data.status === 1 || !data.url) {
          loginUser(loginData, { setSubmitting });
        } else {
          setPdfViewOpen(true);
        }
      },
      onError: (err) => {
        onLoginError(err);
        setSubmitting(false);
      },
    });
  };

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
      remember: true,
    },
    validationSchema: loginFormValidationSchema,
    onSubmit: (loginData, { setSubmitting }) => {    
      if (showCaptcha && !captchaValue) {
        setCaptchaError("Please complete the CAPTCHA to log in.");
        setSubmitting(false);
        return;
      }
      setCaptchaError("");
      onSubmit(loginData, { setSubmitting });
    },
  });

  const {
    errors,
    touched,
    values,
    isSubmitting,
    setSubmitting,
    handleSubmit,
    getFieldProps,
  } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <FormikProvider value={formik}>
      <Form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
        style={{ 
          display: "flex", 
          flexDirection: "column", 
          gap: "1rem",
          width: "100%",
          minHeight: 0,
          flex: 1
        }}
      >
        <Stack spacing={3} sx={{ gap: "1rem" }}>
          <TextField
            fullWidth
            autoComplete="username"
            type="email"
            label="Username"
            InputLabelProps={{ shrink: true }}
            {...getFieldProps("username")}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
            sx={lightInputStyles}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            label="Password"
            InputLabelProps={{ shrink: true }}
            {...getFieldProps("password")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            sx={lightInputStyles}
          />
        </Stack>

        {showCaptcha && (
          <CaptchaField
            value={captchaValue}
            onChange={(value) => {
              setCaptchaValue(value);
              setCaptchaError("");
            }}
            error={captchaError}
          />
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 1 }}
        >
          <FormControlLabel
            sx={{
              '& .MuiFormControlLabel-label': checkboxLabelStyles,
            }}
            control={
              <Checkbox
                {...getFieldProps("remember")}
                checked={values.remember}
                sx={{
                  color: 'rgba(61, 114, 255, 0.4)',
                  '&.Mui-checked': {
                    color: '#3d72ff',
                  },
                }}
              />
            }
            label="Keep me logged in"
          />

          <Link variant="subtitle2" to="/reset" href="/reset" sx={{ color: '#3d72ff', fontWeight: 600 }}>
            Forgot password?
          </Link>
        </Stack>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting || isLoading}
          sx={{ 
            mt: 2, 
            mb: 2,
            minHeight: 48,
            position: 'relative',
            zIndex: 1
          }}
        >
          Log in
        </LoadingButton>
        
      </Form>
      {pdfViewOpen && (
        <Dialog
          fullWidth={true}
          aria-labelledby="customized-dialog-title"
          open={pdfViewOpen}
          sx={{
            backgroundColor: "#0c0a0ad4",
            "& .MuiDialog-container": {
              "& .MuiPaper-root": {
                maxWidth: "1020px", // Set your width here
              },
            },
          }}
        >
          <DialogContent sx={{ padding: 0 }}>
            <>
              <style>
                {`
                    .rpv-core__inner-page {
                        overflow: hidden !important;
                    }
                `}
              </style>
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.6.347/es5/build/pdf.worker.js">
                <div style={{ height: "450px" }}>
                  <Viewer
                    fileUrl={tnCurl} // Use tnCurl for the actual file URL from the state
                    renderPage={(renderPageProps) => (
                      <CustomPageLayer renderPageProps={renderPageProps} />
                    )}
                  />
                </div>
              </Worker>
            </>
            <Box padding="0 20px">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tnCcheckbox}
                    onChange={(e) => setTnCcheckbox(e.target.checked)}
                  />
                }
                label="I have read and agree with the above terms and conditionḍs"
              />
            </Box>
            <Stack
              mt={2}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "1rem",
                padding: "0 20px 20px",
              }}
            >
              <Button
                variant="contained"
                onClick={onAcceptingTnC}
                disabled={!tnCcheckbox}
              >
                Accept
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setPdfViewOpen(false);
                  setSubmitting(false);
                }}
              >
                Cancel
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
      )}
    </FormikProvider>
  );
}
